import DriverPlanListResponse, {
  DriverPlanListResponseDataEntry,
  DriverPlanListResponseDataEntryCheckoutEvent,
  DriverPlanListResponseDataEntryEndNode,
  DriverPlanListResponseDataEntryEndNodeMeta,
  DriverPlanListResponseDataEntryNodeExclusion,
  DriverPlanListResponseDataEntryNodeExclusions,
  DriverPlanListResponseDataEntrySolvedOrder,
  DriverPlanListResponseDataEntrySolvedOrderCargoCompany,
  DriverPlanListResponseDataEntrySolvedOrderRide,
  DriverPlanListResponseDataEntrySolvedOrderRideSeq,
  DriverPlanListResponseDataEntrySolvedOrderRideSeqMeta,
  DriverPlanListResponseDataEntrySolvedOrderTransportingOrder,
  DriverPlanListResponseDataEntrySolvedOrderTransportingOrderCargoOrder,
  DriverPlanListResponseDataEntryStartNode,
  DriverPlanListResponseDataEntryStartNodeMeta,
} from "../../../../api/raily/driver/plan/list/driver-plan-list.response";
import DriverPlanList, {
  DriverPlanListEntry,
  DriverPlanListEntryCheckoutEvent,
  DriverPlanListEntryEndNode,
  DriverPlanListEntryEndNodeMeta,
  DriverPlanListEntryNodeExclusion,
  DriverPlanListEntryNodeExclusions,
  DriverPlanListEntrySolvedOrder,
  DriverPlanListEntrySolvedOrderCargoCompany,
  DriverPlanListEntrySolvedOrderRide,
  DriverPlanListEntrySolvedOrderRideSeq,
  DriverPlanListEntrySolvedOrderRideSeqMeta,
  DriverPlanListEntrySolvedOrderTransportingOrder,
  DriverPlanListEntrySolvedOrderTransportingOrderCargoOrder,
  DriverPlanListEntryStartNode,
  DriverPlanListEntryStartNodeMeta,
} from "./driver-plan-list";

const createEntryCheckoutEvent = (
  checkoutEvent: DriverPlanListResponseDataEntryCheckoutEvent,
): DriverPlanListEntryCheckoutEvent => {
  return {
    achievedAt: checkoutEvent.achievedAt,
    createdAt: checkoutEvent.createdAt,
    createdByName: checkoutEvent.createdByName,
    haltingTime: checkoutEvent.haltingTime,
    highwayCharge: checkoutEvent.highwayCharge,
    id: checkoutEvent.id,
    nodeId: checkoutEvent.nodeId,
  };
};

const createEntryCheckoutEvents = (
  checkoutEvents: DriverPlanListResponseDataEntryCheckoutEvent[],
): DriverPlanListEntryCheckoutEvent[] => {
  return checkoutEvents.map(createEntryCheckoutEvent);
};

const createEntryEndNodeMeta = (
  meta: DriverPlanListResponseDataEntryEndNodeMeta,
): DriverPlanListEntryEndNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryEndNode = (
  endNode: DriverPlanListResponseDataEntryEndNode,
): DriverPlanListEntryEndNode => {
  return {
    displayName: endNode.displayName,
    haltingTime: endNode.haltingTime,
    lat: endNode.lat,
    lon: endNode.lon,
    meta: createEntryEndNodeMeta(endNode.meta),
    time: endNode.time,
  };
};

const createEntryStartNodeMeta = (
  meta: DriverPlanListResponseDataEntryStartNodeMeta,
): DriverPlanListEntryStartNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryStartNode = (
  startNode: DriverPlanListResponseDataEntryStartNode,
): DriverPlanListEntryStartNode => {
  return {
    displayName: startNode.displayName,
    haltingTime: startNode.haltingTime,
    lat: startNode.lat,
    lon: startNode.lon,
    meta: createEntryStartNodeMeta(startNode.meta),
    time: startNode.time,
  };
};

const createEntryNodeExclusion = (
  nodeExclusion: DriverPlanListResponseDataEntryNodeExclusion,
): DriverPlanListEntryNodeExclusion => {
  return {
    excludeApproaching: nodeExclusion.excludeApproaching,
    excludeInside: nodeExclusion.excludeInside,
    excludeReturning: nodeExclusion.excludeReturning,
    nodeIds: nodeExclusion.nodeIds,
  };
};

const createEntryNodeExclusions = (
  nodeExclusions: DriverPlanListResponseDataEntryNodeExclusion[],
): DriverPlanListEntryNodeExclusion[] => {
  return nodeExclusions.map(createEntryNodeExclusion);
};

const createEntryNode = (
  node: DriverPlanListResponseDataEntryNodeExclusions,
): DriverPlanListEntryNodeExclusions => {
  return {
    exclusions: createEntryNodeExclusions(node.exclusions),
  };
};

const createEntrySolvedOrderRideSeqItemMeta = (
  meta: DriverPlanListResponseDataEntrySolvedOrderRideSeqMeta,
): DriverPlanListEntrySolvedOrderRideSeqMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntrySolvedOrderRideSeqItem = (
  item: DriverPlanListResponseDataEntrySolvedOrderRideSeq,
): DriverPlanListEntrySolvedOrderRideSeq => {
  return {
    displayName: item.displayName,
    haltingTime: item.haltingTime,
    lat: item.lat,
    lon: item.lon,
    meta: createEntrySolvedOrderRideSeqItemMeta(item.meta),
    time: item.time,
  };
};

const createEntrySolvedOrderRideSeq = (
  seq: DriverPlanListResponseDataEntrySolvedOrderRideSeq[],
): DriverPlanListEntrySolvedOrderRideSeq[] => {
  return seq.map(createEntrySolvedOrderRideSeqItem);
};

const createEntrySolvedOrderRide = (
  ride: DriverPlanListResponseDataEntrySolvedOrderRide,
): DriverPlanListEntrySolvedOrderRide => {
  return {
    seq: createEntrySolvedOrderRideSeq(ride.seq),
  };
};

const createEntrySolvedOrderTransportingOrderCargoOrder = (
  cargoOrder: DriverPlanListResponseDataEntrySolvedOrderTransportingOrderCargoOrder,
): DriverPlanListEntrySolvedOrderTransportingOrderCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
    id: cargoOrder.id,
  };
};

const createEntrySolvedOrderTransportingOrder = (
  transportingOrder: DriverPlanListResponseDataEntrySolvedOrderTransportingOrder,
): DriverPlanListEntrySolvedOrderTransportingOrder => {
  return {
    cargoOrder: createEntrySolvedOrderTransportingOrderCargoOrder(
      transportingOrder.cargoOrder,
    ),
  };
};

const createEntrySolvedOrderCargoCompany = (
  cargoCompany: DriverPlanListResponseDataEntrySolvedOrderCargoCompany,
): DriverPlanListEntrySolvedOrderCargoCompany => {
  return {
    displayName: cargoCompany.displayName,
  };
};

const createEntrySolvedOrder = (
  solvedOrder: DriverPlanListResponseDataEntrySolvedOrder,
): DriverPlanListEntrySolvedOrder => {
  return {
    cargoCompany: createEntrySolvedOrderCargoCompany(solvedOrder.cargoCompany),
    id: solvedOrder.id,
    ride: createEntrySolvedOrderRide(solvedOrder.ride),
    transportingOrder: createEntrySolvedOrderTransportingOrder(
      solvedOrder.transportingOrder,
    ),
  };
};

const createEntrySolvedOrders = (
  solvedOrders: DriverPlanListResponseDataEntrySolvedOrder[],
): DriverPlanListEntrySolvedOrder[] => {
  return solvedOrders.map(createEntrySolvedOrder);
};

const createEntry = (
  entry: DriverPlanListResponseDataEntry,
): DriverPlanListEntry => {
  return {
    checkoutEvents: createEntryCheckoutEvents(entry.checkoutEvents),
    endNode: createEntryEndNode(entry.endNode),
    humanId: entry.humanId,
    id: entry.id,
    nodeExclusions: createEntryNode(entry.nodeExclusions),
    solvedOrders: createEntrySolvedOrders(entry.solvedOrders),
    startNode: createEntryStartNode(entry.startNode),
  };
};

const createEntries = (
  entries: DriverPlanListResponseDataEntry[],
): DriverPlanListEntry[] => {
  return entries.map(createEntry);
};

const create = (response: DriverPlanListResponse): DriverPlanList => {
  return {
    entries: createEntries(response.data.entries),
  };
};

const driverPlanListFactory = {
  create,
};

export default driverPlanListFactory;
